/** Criar oportunidade (SPEC-OPP-001).
 *
 * **Só o título é obrigatório**, e isso é regra de produto, não economia de
 * código: formulário longo faz a pessoa cadastrar por fora e voltar depois — e
 * o "depois" não chega. Etapa, valor e prazo são opcionais porque quem está
 * registrando uma conversa que acabou de acontecer raramente sabe os três.
 */

import { useState, type SyntheticEvent } from "react";

import { ApiError } from "@/lib/http";
import { Botao } from "@/shared/ui/Botao";
import { Campo } from "@/shared/ui/Campo";
import { Selecao } from "@/shared/ui/Selecao";

import { centavos, mascararMoeda } from "./formato";
import { useCriarOportunidade, usePessoas } from "./useQuadro";

interface Props {
  etapas: readonly { id: string; nome: string }[];
  /** Etapa já escolhida por quem abriu o formulário pelo botão da coluna.
   * Quem clicou ali já disse onde quer o card; repetir a pergunta seria pedir
   * duas vezes a mesma decisão. */
  etapaInicial?: string;
  /** Chamado com o id da oportunidade criada, antes de fechar. É o que permite
   * ao board saber o que esperar da projeção em vez de reler no escuro. */
  aoCriar?: (id: string) => void;
  aoFechar: () => void;
}

export function NovaOportunidade({ etapas, etapaInicial, aoCriar, aoFechar }: Props) {
  const [titulo, setTitulo] = useState("");
  const [valor, setValor] = useState("");
  const [etapa, setEtapa] = useState(etapaInicial ?? "");
  const [dono, setDono] = useState("");
  const criacao = useCriarOportunidade();
  const pessoas = usePessoas();

  const podeEnviar = titulo.trim() !== "" && !criacao.isPending;

  async function enviar(evento: SyntheticEvent) {
    evento.preventDefault();
    if (!podeEnviar) return;

    try {
      const criada = await criacao.mutateAsync({
        title: titulo.trim(),
        ...(etapa === "" ? {} : { stage_id: etapa }),
        ...(centavos(valor) === null ? {} : { estimated_value_cents: centavos(valor) }),
        // Omitido, o servidor assume quem está cadastrando.
        ...(dono === "" ? {} : { owner_id: dono }),
      });
      aoCriar?.(criada.id);
      aoFechar();
    } catch {
      // A mensagem sai de `criacao.error`; engolir aqui evita o erro não
      // tratado no console, que não diz nada a mais a quem está usando.
    }
  }

  return (
    <form
      onSubmit={(evento) => void enviar(evento)}
      aria-label="Nova oportunidade"
      // Grade em vez de `flex-wrap`, e `items-end` sobre controles de mesma
      // altura: antes o "Opcional" abaixo do valor empurrava aquele campo para
      // cima, e cada controle terminava numa linha de base diferente. Empilha
      // em tela estreita sem que nada mude de tamanho no meio do caminho.
      className="grid grid-cols-1 items-end gap-4 rounded-lg border border-borda bg-superficie p-4 shadow-e1 sm:grid-cols-2 lg:grid-cols-[minmax(220px,1fr)_8rem_11rem_11rem_auto]"
    >
      <Campo
        rotulo="Título"
        value={titulo}
        onChange={(evento) => {
          setTitulo(evento.target.value);
        }}
        placeholder="Renovação Acme 2027"
        required
        autoFocus
      />

      {/* Sem "Opcional" embaixo: o asterisco do título já diz o que é
          obrigatório, e marcar as duas coisas é redundante — além de ser o
          que desalinhava a linha. */}
      {/* A máscara é aplicada no `onChange`, e não só na saída: o campo mostra
          o número agrupado enquanto se digita, que é como a pessoa confere se
          escreveu cinco mil ou cinquenta mil sem contar zeros na tela. */}
      <Campo
        rotulo="Valor (R$)"
        inputMode="decimal"
        value={valor}
        onChange={(evento) => {
          setValor(mascararMoeda(evento.target.value));
        }}
        placeholder="85.000"
      />

      {etapas.length > 0 && (
        <Selecao
          rotulo="Etapa"
          value={etapa}
          onChange={(evento) => {
            setEtapa(evento.target.value);
          }}
        >
          <option value="">Primeira do funil</option>
          {etapas.map((item) => (
            <option key={item.id} value={item.id}>
              {item.nome}
            </option>
          ))}
        </Selecao>
      )}

      {/* Quem abriu a frente — não necessariamente quem está cadastrando.
          O padrão é quem cadastra, porque é o caso comum; a escolha existe
          para o caso que importa: a oportunidade nasce com o dono certo, todo
          mundo vê de quem é, e ninguém liga por cima de contato já firmado. */}
      <Selecao
        rotulo="Quem abriu"
        value={dono}
        disabled={pessoas.data === undefined}
        onChange={(evento) => {
          setDono(evento.target.value);
        }}
      >
        <option value="">Eu</option>
        {(pessoas.data ?? []).map((pessoa) => (
          <option key={pessoa.id} value={pessoa.id}>
            {pessoa.name}
          </option>
        ))}
      </Selecao>

      <div className="flex gap-2">
        <Botao type="submit" carregando={criacao.isPending} disabled={!podeEnviar}>
          Criar
        </Botao>
        <Botao type="button" variante="fantasma" onClick={aoFechar}>
          Cancelar
        </Botao>
      </div>

      {criacao.isError && (
        <p role="alert" className="font-texto text-caption text-carmim sm:col-span-full">
          {mensagem(criacao.error)}
        </p>
      )}
    </form>
  );
}

function mensagem(erro: unknown): string {
  if (erro instanceof ApiError) return erro.message;
  return "Não foi possível criar a oportunidade. Tente de novo.";
}
